import type {
  NotificationCenterProps,
  NotificationFilter,
  NotificationType,
} from '@/types/notification'
import { CheckCheck, Filter, Loader2, Search, X } from 'lucide-react'
import * as React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { NotificationItem } from '@/components/ui/notification-item'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { NOTIFICATION_TYPE_LABELS } from '@/types/notification'

export function NotificationCenter({
  open,
  onOpenChange,
  notifications,
  loading = false,
  onMarkAsRead,
  onMarkAllAsRead,
  onLoadMore,
  hasMore = false,
  totalCount = 0,
}: NotificationCenterProps) {
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
  const [filters, setFilters] = React.useState<NotificationFilter>({})
  const [searchQuery, setSearchQuery] = React.useState('')
  const [loadingMore, setLoadingMore] = React.useState(false)

  // Filter notifications based on current filters and search
  const filteredNotifications = React.useMemo(() => {
    let filtered = [...notifications]

    // Filter by type
    if (filters.type) {
      filtered = filtered.filter(n => n.type === filters.type)
    }

    // Filter by read status
    if (filters.status) {
      filtered = filtered.filter(n => (filters.status === 'unread' ? !n.readAt : !!n.readAt))
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        n => n.title.toLowerCase().includes(query) || n.message.toLowerCase().includes(query),
      )
    }

    return filtered
  }, [notifications, filters, searchQuery])

  const unreadCount = notifications.filter(n => !n.readAt).length
  const hasSelectedItems = selectedIds.size > 0
  const allSelected
    = filteredNotifications.length > 0 && filteredNotifications.every(n => selectedIds.has(n.id))

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      setSelectedIds(new Set(filteredNotifications.map(n => n.id)))
    }
    else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectNotification = (id: string, selected: boolean) => {
    const newSelectedIds = new Set(selectedIds)
    if (selected) {
      newSelectedIds.add(id)
    }
    else {
      newSelectedIds.delete(id)
    }
    setSelectedIds(newSelectedIds)
  }

  const handleMarkSelectedAsRead = () => {
    if (selectedIds.size > 0) {
      // onMarkAsRead accepts an array of IDs for both single and bulk operations
      onMarkAsRead(Array.from(selectedIds))
      setSelectedIds(new Set())
    }
  }

  const handleLoadMore = async () => {
    if (onLoadMore && !loadingMore) {
      setLoadingMore(true)
      await onLoadMore()
      setLoadingMore(false)
    }
  }

  const handleClearFilters = () => {
    setFilters({})
    setSearchQuery('')
  }

  const hasActiveFilters = filters.type || filters.status || searchQuery

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] h-[80vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl">Notification Center</DialogTitle>
          <DialogDescription>Manage and review all your notifications</DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-4 space-y-4">
          {/* Search and filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select
              value={filters.type || 'all'}
              onValueChange={value =>
                setFilters(prev => ({
                  ...prev,
                  type: value === 'all' ? undefined : (value as NotificationType),
                }))}
            >
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.status || 'all'}
              onValueChange={value =>
                setFilters(prev => ({
                  ...prev,
                  status: value === 'all' ? undefined : (value as 'read' | 'unread'),
                }))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active filters */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {filters.type && (
                <Badge variant="secondary">
                  Type:
                  {NOTIFICATION_TYPE_LABELS[filters.type]}
                </Badge>
              )}
              {filters.status && (
                <Badge variant="secondary">
                  Status:
                  {filters.status}
                </Badge>
              )}
              {searchQuery && (
                <Badge variant="secondary">
                  Search:
                  {searchQuery}
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="h-6 px-2">
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            </div>
          )}

          {/* Bulk actions bar */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={allSelected ? true : hasSelectedItems ? 'indeterminate' : false}
                onCheckedChange={handleSelectAll}
                aria-label="Select all notifications"
              />
              <span className="text-sm text-muted-foreground">
                {hasSelectedItems
                  ? `${selectedIds.size} selected`
                  : `${filteredNotifications.length} notifications`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {hasSelectedItems && (
                <Button variant="outline" size="sm" onClick={handleMarkSelectedAsRead}>
                  <CheckCheck className="mr-2 h-4 w-4" />
                  Mark selected as read
                </Button>
              )}

              {unreadCount > 0 && (
                <Button variant="outline" size="sm" onClick={onMarkAllAsRead}>
                  <CheckCheck className="mr-2 h-4 w-4" />
                  Mark all as read
                </Button>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Notifications list */}
        <ScrollArea className="flex-1 min-h-0 h-full min-w-0 px-6">
          {loading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-muted-foreground">
                {hasActiveFilters ? 'No notifications match your filters' : 'No notifications yet'}
              </p>
              {hasActiveFilters && (
                <Button variant="link" size="sm" onClick={handleClearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="pb-4" role="list">
              {filteredNotifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={id => onMarkAsRead([id])} // Pass as array for consistency
                  selected={selectedIds.has(notification.id)}
                  onSelect={handleSelectNotification}
                  className="border-b last:border-b-0"
                />
              ))}

              {hasMore && (
                <div className="pt-4 text-center">
                  <Button variant="outline" onClick={handleLoadMore} disabled={loadingMore}>
                    {loadingMore
                      ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        )
                      : (
                          'Load more'
                        )}
                  </Button>
                  {totalCount > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Showing
                      {' '}
                      {filteredNotifications.length}
                      {' '}
                      of
                      {' '}
                      {totalCount}
                      {' '}
                      notifications
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
